import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Building2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { Estate } from '../../types';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface EstateForm {
  name: string;
  blocksText: string;
}

const emptyForm = (): EstateForm => ({ name: '', blocksText: '' });

export default function Estates() {
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [modal, setModal] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [form, setForm] = useState<EstateForm>(emptyForm());
  const [formSaving, setFormSaving] = useState(false);

  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<Estate[]>('/estates')
      .then(setEstates)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd = () => {
    setError('');
    setEditingIdx(null);
    setForm(emptyForm());
    setModal(true);
  };

  const openEdit = (idx: number) => {
    setError('');
    setEditingIdx(idx);
    setForm({ name: estates[idx].name, blocksText: estates[idx].blocks.join(', ') });
    setModal(true);
  };

  const parseBlocks = (text: string): string[] =>
    text.split(',').map((b) => b.trim()).filter(Boolean);

  const saveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSaving(true);
    setError('');
    try {
      const updated = [...estates];
      const entry: Estate = { name: form.name, blocks: parseBlocks(form.blocksText) };
      if (editingIdx !== null) {
        updated[editingIdx] = entry;
      } else {
        updated.push(entry);
      }
      await api.put('/estates', { estates: updated });
      setEstates(updated);
      setModal(false);
      setMessage(editingIdx !== null ? 'Estate updated.' : 'Estate added.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setFormSaving(false);
    }
  };

  const removeEstate = async (idx: number) => {
    try {
      const updated = estates.filter((_, i) => i !== idx);
      await api.put('/estates', { estates: updated });
      setEstates(updated);
      setDeleteIdx(null);
      setMessage('Estate removed.');
    } catch (err) {
      setError((err as Error).message);
      setDeleteIdx(null);
    }
  };

  if (loading) return <Spinner label="Loading estates..." />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold">Estates</h1>
        <div className="flex items-center space-x-3">
          <button onClick={openAdd} className={primaryBtn}>
            <Plus className="h-4 w-4" /> Add Estate
          </button>
          <button
            onClick={async () => {
              setSaving(true);
              setError('');
              setMessage('');
              try {
                await api.put('/estates', { estates });
                setMessage('All estates saved successfully.');
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
            className={primaryBtn}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 -mt-4">
        Manage the list of estates and their blocks. Use the save button to persist all changes.
      </p>

      {message && <div className="px-4 py-3 rounded-lg bg-green-50 text-green-800 text-sm">{message}</div>}
      {error && <Alert>{error}</Alert>}

      {estates.length === 0 ? (
        <EmptyState message="No estates yet. Click Add Estate to create one." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {estates.map((estate, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-5 flex flex-col justify-between">
              <div>
                <div className="flex items-center mb-3">
                  <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                  <p className="font-semibold text-lg">{estate.name}</p>
                </div>
                {estate.blocks.length > 0 ? (
                  <ul className="space-y-1">
                    {estate.blocks.map((block) => (
                      <li key={block} className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-1.5">
                        {block}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic">No blocks</p>
                )}
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button onClick={() => openEdit(idx)} className="p-2 rounded-lg hover:bg-primary/10 text-primary" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => setDeleteIdx(idx)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editingIdx !== null ? 'Edit Estate' : 'Add Estate'}>
        {error && <Alert>{error}</Alert>}
        <form onSubmit={saveForm} className="space-y-4">
          <div>
            <label className={labelClass}>Estate Name *</label>
            <input
              className={inputClass}
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Naya Estate"
            />
          </div>
          <div>
            <label className={labelClass}>Blocks (comma-separated)</label>
            <input
              className={inputClass}
              value={form.blocksText}
              onChange={(e) => setForm({ ...form, blocksText: e.target.value })}
              placeholder="e.g. Block A, Block B, Block C"
            />
            <p className="text-xs text-gray-400 mt-1">Separate block names with commas.</p>
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModal(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={formSaving} className={primaryBtn}>
              {formSaving ? 'Saving...' : editingIdx !== null ? 'Save Changes' : 'Add Estate'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleteIdx !== null}
        onClose={() => setDeleteIdx(null)}
        onConfirm={() => deleteIdx !== null && removeEstate(deleteIdx)}
        title="Remove Estate"
        message="Are you sure you want to remove this estate? This action will be saved when you click Save."
      />
    </div>
  );
}
